import { useEffect, useMemo, useState } from 'react'
import {
  getCatalogCivilitiesV2,
  getCatalogCustomerTypesV2,
  getCatalogLanguagesV2,
  getCatalogProjectTypesV2,
} from '@/lib/api/v2/catalog'

export type IdLabelOption = { id: string; label: string; icon?: string | null }

function toOptions(items: Array<{ id?: string | null; label?: string | null; icon?: string | null }>): IdLabelOption[] {
  return items
    .filter((x) => !!x.id)
    .map((x) => ({ id: x.id as string, label: x.label || String(x.id), icon: x.icon || null }))
}

export function useProjectCreateCatalogV2() {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  const [projectTypesRaw, setProjectTypesRaw] = useState<Array<{ id?: string | null; label?: string | null; icon?: string | null }>>([])
  const [languagesRaw, setLanguagesRaw] = useState<Array<{ id?: string | null; label?: string | null; icon?: string | null }>>([])
  const [civilitiesRaw, setCivilitiesRaw] = useState<Array<{ id?: string | null; label?: string | null; icon?: string | null }>>([])
  const [customerTypesRaw, setCustomerTypesRaw] = useState<Array<{ id?: string | null; label?: string | null; icon?: string | null }>>([])

  useEffect(() => {
    let mounted = true

    async function load() {
      try {
        setIsLoading(true)
        const [projectTypes, languages, civilities, customerTypes] = await Promise.all([
          getCatalogProjectTypesV2(),
          getCatalogLanguagesV2(),
          getCatalogCivilitiesV2(),
          getCatalogCustomerTypesV2(),
        ])

        if (!mounted) return
        setProjectTypesRaw(projectTypes)
        setLanguagesRaw(languages)
        setCivilitiesRaw(civilities)
        setCustomerTypesRaw(customerTypes)
        setError(null)
      } catch (e) {
        if (!mounted) return
        setError(e as Error)
      } finally {
        if (mounted) setIsLoading(false)
      }
    }

    load()
    return () => {
      mounted = false
    }
  }, [])

  const projectTypeOptions = useMemo(() => toOptions(projectTypesRaw), [projectTypesRaw])
  const languageOptions = useMemo(() => toOptions(languagesRaw), [languagesRaw])
  const civilityOptions = useMemo(() => toOptions(civilitiesRaw), [civilitiesRaw])
  const customerTypeOptions = useMemo(() => toOptions(customerTypesRaw), [customerTypesRaw])

  return {
    isLoading,
    error,
    projectTypeOptions,
    languageOptions,
    civilityOptions,
    customerTypeOptions,
  }
}
